/**
 * LayoutManager — gridstack lifecycle + cube content renderer.
 * UI-PHASE-16.A.
 *
 * Responsibilities:
 *   1. Build a single GridStack inside the provided root element on
 *      first mount.
 *   2. Add one gridstack widget per CubeDef (CUBES), placing it at
 *      the saved-layout geometry if we have one in localStorage,
 *      else at its defaultGeometry.
 *   3. On every store update, walk every mounted widget and re-render
 *      the cube's TemplateResult into its content div. lit-html is
 *      idempotent, so this is cheap.
 *   4. Persist the layout to localStorage on every change/resize/move
 *      event.
 *   5. Expose resetLayout() so a header button can wipe the saved
 *      layout and revert to defaults.
 *
 * The center "tabs" cube is special — its render needs activeTab +
 * onChange. The manager keeps that state and passes a custom render
 * function that captures it. Tab switches trigger paintAll() like any
 * store update.
 */
import { render, type TemplateResult } from "lit-html";
import { GridStack, type GridStackOptions, type GridItemHTMLElement }
  from "gridstack";
import "gridstack/dist/gridstack.min.css";

import { CUBES, type CubeDef, type CubeGeometry } from "./cubes";
import { renderCenterStage } from "../components/CenterStage";
import type { TabId } from "../App";

const STORAGE_KEY = "console.layout.v3";  // v3 = bigger defaults, invisible shell

// Tab list used by the center stage cube. Kept here so the cube
// registry doesn't have to know about it.
const TABS: { id: TabId; label: string; phase: 1 | 2 | 3 | 4 | 5 | 6 }[] = [
  { id: "schedule",    label: "Schedule",       phase: 1 },
  { id: "pipeline",    label: "Pipeline",       phase: 2 },
  { id: "calibration", label: "Calibration",    phase: 2 },
  { id: "cost",        label: "Cost",           phase: 3 },
  { id: "bets",        label: "Bet inspector",  phase: 2 },
  { id: "prompts",     label: "Prompts",        phase: 3 },
  { id: "strategies",  label: "Strategies",     phase: 4 },
  { id: "replay",      label: "Slate replay",   phase: 4 },
  { id: "rotation",    label: "HR rotation",    phase: 4 },
  { id: "rewind",      label: "Rewind",         phase: 5 },
  { id: "alerts",      label: "Alerts",         phase: 5 },
  { id: "experiments", label: "Experiments",    phase: 5 },
  { id: "lineups",     label: "Lineups",        phase: 5 },
  { id: "preview",     label: "Mobile preview", phase: 6 },
  { id: "eggs",        label: "Easter eggs",    phase: 6 },
  { id: "todo",        label: "TODOs",          phase: 6 },
  { id: "diff",        label: "Dashboard diff", phase: 6 },
];

interface SavedItem {
  id: string;
  x?: number;
  y?: number;
  w?: number;
  h?: number;
}

export class LayoutManager {
  private grid: GridStack | null = null;
  private activeTab: TabId = "schedule";
  /** Map cube id → its content div (the inner render target). */
  private contentEls = new Map<string, HTMLElement>();

  mount(root: HTMLElement): void {
    // Build the .grid-stack container and seed it with empty widgets.
    // Each widget's content div is a stable target we keep rendering
    // into across the lifetime of the page.
    root.classList.add("grid-stack-host");
    const stackEl = document.createElement("div");
    stackEl.className = "grid-stack";
    root.appendChild(stackEl);

    const opts: GridStackOptions = {
      column: 12,
      cellHeight: 36,
      margin: 8,
      float: true,            // widgets stay where dropped, no compacting
      animate: true,
      handle: ".cube-shell__drag",
      resizable: { handles: "se,sw,e,s,w" },
      // Stacked moves feel best with a small offset; the default works.
    };
    this.grid = GridStack.init(opts, stackEl);

    const saved = this.loadSaved();

    for (const cube of CUBES) {
      const geom = saved?.[cube.id] ?? cube.defaultGeometry;
      this.addWidget(cube, geom);
    }

    // Persist on any user-initiated change (drag, resize, drop).
    this.grid.on("change", () => this.persist());
    this.grid.on("resizestop", () => this.persist());
    this.grid.on("dragstop", () => this.persist());
  }

  /** Called on every store update. Re-renders each cube's content into
   *  its stable target. */
  paintAll(): void {
    for (const cube of CUBES) {
      const target = this.contentEls.get(cube.id);
      if (!target) continue;
      const tpl = this.renderCube(cube);
      render(tpl, target);
    }
  }

  setActiveTab(tab: TabId): void {
    this.activeTab = tab;
    this.paintAll();
  }

  resetLayout(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
    // Reloading is the cleanest reset — keeps Gridstack init logic
    // simple and ensures every cube reverts cleanly to its default.
    location.reload();
  }

  // -----------------------------------------------------------------

  private addWidget(cube: CubeDef, geom: CubeGeometry): void {
    if (!this.grid) return;
    // Build the widget shell DOM manually so we can keep a stable
    // reference to the content div.
    const item = document.createElement("div");
    item.classList.add("grid-stack-item");
    item.setAttribute("gs-id", cube.id);
    item.setAttribute("gs-x", String(geom.x));
    item.setAttribute("gs-y", String(geom.y));
    item.setAttribute("gs-w", String(geom.w));
    item.setAttribute("gs-h", String(geom.h));
    if (geom.minW != null) item.setAttribute("gs-min-w", String(geom.minW));
    if (geom.minH != null) item.setAttribute("gs-min-h", String(geom.minH));
    if (geom.locked) {
      item.setAttribute("gs-no-move", "true");
      item.setAttribute("gs-no-resize", "true");
    }

    const contentWrap = document.createElement("div");
    contentWrap.classList.add("grid-stack-item-content");

    const shell = document.createElement("div");
    shell.classList.add("cube-shell");
    shell.dataset.cubeId = cube.id;
    // No header bar — the panel inside owns the visible title. The
    // drag handle is a small floating overlay that fades in on hover,
    // so the panel's original aesthetic shows through untouched.
    shell.innerHTML = `
      <span class="cube-shell__drag" title="Drag to rearrange · ${escapeHtml(cube.title)}">
        <span></span><span></span><span></span>
        <span></span><span></span><span></span>
      </span>
      <div class="cube-shell__body"></div>
    `;
    const body = shell.querySelector(".cube-shell__body") as HTMLElement;

    contentWrap.appendChild(shell);
    item.appendChild(contentWrap);

    // Must append to the grid container BEFORE makeWidget — gridstack's
    // internal accounting relies on parentElement being set when it
    // reads geometry from the gs-* attributes.
    (this.grid.el as HTMLElement).appendChild(item);
    this.grid.makeWidget(item);

    this.contentEls.set(cube.id, body);
  }

  private renderCube(cube: CubeDef): TemplateResult {
    if (cube.id === "tabs") {
      return renderCenterStage({
        tabs: TABS,
        activeTab: this.activeTab,
        onTabChange: (id) => this.setActiveTab(id),
      });
    }
    return cube.render();
  }

  private loadSaved(): Record<string, CubeGeometry> | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const items = JSON.parse(raw) as SavedItem[];
      if (!Array.isArray(items)) return null;
      const out: Record<string, CubeGeometry> = {};
      for (const it of items) {
        if (!it.id) continue;
        // Defaults are partial — only override the fields gridstack saved.
        const cube = CUBES.find((c) => c.id === it.id);
        if (!cube) continue;
        out[it.id] = {
          ...cube.defaultGeometry,
          ...(it.x != null ? { x: it.x } : {}),
          ...(it.y != null ? { y: it.y } : {}),
          ...(it.w != null ? { w: it.w } : {}),
          ...(it.h != null ? { h: it.h } : {}),
        };
      }
      return out;
    } catch (e) {
      console.warn("[layout] failed to load saved layout:", e);
      return null;
    }
  }

  private persist(): void {
    if (!this.grid) return;
    try {
      // Walk the gridstack engine for current geometry — save() returns
      // the descriptor with content baked in, which is wasteful; we
      // just need id + position.
      const items = this.grid.engine.nodes.map((n) => {
        const el = n.el as GridItemHTMLElement | undefined;
        const id = el?.getAttribute("gs-id") ?? "";
        return { id, x: n.x, y: n.y, w: n.w, h: n.h };
      }).filter((i) => i.id);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.warn("[layout] persist failed:", e);
    }
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (ch) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;",
    "\"": "&quot;", "'": "&#39;",
  })[ch] ?? ch);
}

export const layoutManager = new LayoutManager();
